import { CboRole, Req, Res, SiteInfo } from '@cbo/shared-library';
import { TFunction } from 'react-i18next';
import { matchPath } from 'react-router-dom';
import { formatDate, formatTime } from '../../utils';
import routes from '../../constants/routes';

type siteRoutes = {
  basePath: string;
  euPermission: CboRole[];
  orgPermission: CboRole[];
};

export const formatAddress = (
  address: Req.SiteManagement.SiteAddressData | undefined,
  t: TFunction<'translation', undefined>
) => {
  if (!address) {
    return '-';
  }

  const { street, city, state, postalCode, country } = address;
  if (address.country === 'USA') {
    return t('admin.allSitesDatagrid.addressFormat', {
      street,
      city,
      state,
      postalCode,
    });
  }

  const addressParts = [street, city, state, postalCode, country].filter((value) => !!value);
  return addressParts.join(', ');
};

export const formatTimeStamp = (date: Date | string, preferences: Res.Admin.UserPreferences | null) => {
  if (!date) {
    return '-';
  }

  const formattedDate = formatDate(date, preferences);
  const formattedTime = formatTime(date, preferences);

  return `${formattedDate} ${formattedTime}`;
};

export const getSiteButtonText = (
  selectedSiteName: string,
  multiSelectedSites: SiteInfo[],
  allSitesLength: number,
  isSiteFiltering: boolean,
  isHeader: boolean,
  sitesText: string,
  allSitesText: string
) => {
  let buttonText = '';
  if (isHeader) {
    buttonText = selectedSiteName;
    if (isSiteFiltering && multiSelectedSites.length > 0)
      buttonText = `${multiSelectedSites.length} ${sitesText.toLowerCase()}`;
    if (isSiteFiltering && multiSelectedSites.length === 1 && multiSelectedSites[0].name)
      buttonText = multiSelectedSites[0].name;
    else if (isSiteFiltering && multiSelectedSites.length === allSitesLength) buttonText = allSitesText;
  } else {
    buttonText = `${multiSelectedSites.length} ${sitesText.toLowerCase()}`;
    if (multiSelectedSites.length === 1 && multiSelectedSites[0].name) buttonText = multiSelectedSites[0].name;
    if (multiSelectedSites.length === 0) buttonText = selectedSiteName;
    else if (multiSelectedSites.length === allSitesLength) buttonText = allSitesText;
  }
  return buttonText;
};

export const orgRoutes: string[] = [
  routes.SITES,
  routes.FISCAL_CALENDAR,
  routes.NOTIFICATION_SETTINGS,
  routes.ACTIVITY_LOG,
  routes.LABOR_RULES,
  routes.INVOICE_HISTORY_REPORT,
  routes.UNFINALIZED_GL_ACCOUNTS_REPORT,
  routes.UNFINALIZED_VENDORS_REPORT,
  routes.GL_ACCOUNT_MAPPING,
  routes.MANAGE_GL_ACCOUNTS,
  routes.HOUSE_ACCOUNTS,
  routes.SALES_DEFINITIONS_SETTINGS,
  routes.LABOR_SETTINGS_SCHEDULE,
  routes.JOBCODE_CONFIGURATION_PAGE,
  routes.TIP_SHARE_SETTINGS,
  routes.SALES_ITEMS,
  routes.MODIFIER_GROUPS,
  routes.SALES_COMPARATIVE_SALES_LABOR_REPORT,
  routes.EDIT_VENDOR_BASE,
  routes.CUSTOM_METRICS,
  routes.INVENTORY_VALUATION_SETTINGS,
];

export const cfcOrgRoutes: string[] = [routes.JOBCODE_CONFIGURATION_PAGE];

// Routes where the AppBar SitesSwitcher should allow parent (Corp/Global) EU selection for CFC orgs,
// See ASM-9140.
export const auditHierarchyCfcRoutes: string[] = [routes.ACTIVITY_LOG];

export const allSitesRoutes: siteRoutes[] = [
  {
    basePath: routes.RECIPES,
    euPermission: [CboRole.RECIPE_EU_VIEWER],
    orgPermission: [CboRole.RECIPE_ORG_VIEWER],
  },
  { basePath: routes.VENDORS, euPermission: [CboRole.VENDOR_EU_VIEW], orgPermission: [CboRole.VENDOR_ORG_VIEW] },
  {
    basePath: routes.LOCATIONS,
    euPermission: [CboRole.MOBILE_INVENTORY_VIEW],
    orgPermission: [CboRole.MOBILE_INVENTORY_VIEW],
  },
  {
    basePath: routes.INVENTORY_LISTS,
    euPermission: [CboRole.MOBILE_INVENTORY_VIEW],
    orgPermission: [CboRole.MOBILE_INVENTORY_VIEW],
  },
];

export const allSitesRoutesWithOldPermisssions: siteRoutes[] = [
  {
    basePath: routes.RECIPES,
    euPermission: [CboRole.RECIPE_VIEWER],
    orgPermission: [CboRole.RECIPE_CREATOR],
  },
  // for above store manager has create permissions in vendors to fix this checking with recipe
  { basePath: routes.VENDORS, euPermission: [CboRole.VENDOR_VIEW], orgPermission: [CboRole.RECIPE_CREATOR] },
];

export const allSiteRouteWithRoles = [
  {
    basePath: routes.RECIPES,
    newRole: [CboRole.RECIPE_ORG_VIEWER],
    oldRole: [CboRole.RECIPE_CREATOR],
  },
  {
    basePath: routes.VENDORS,
    newRole: [CboRole.VENDOR_ORG_VIEW],
    oldRole: [CboRole.RECIPE_CREATOR],
  },
  {
    basePath: routes.RAW_ITEMS,
    newRole: [CboRole.RAW_ITEM_ORG_VIEW],
    oldRole: [CboRole.RAW_ITEM_CREATE],
  },
  {
    basePath: routes.LOCATIONS,
    newRole: [CboRole.MOBILE_INVENTORY_VIEW],
    oldRole: [CboRole.MOBILE_INVENTORY_VIEW],
  },
];

// Routes where BspSiteSwitcherWrapper must NOT overwrite the siteConfiguration set by the page itself.
// The Audit Log page manages its own config via AuditActivityLoggingPage. See ASM-9140.
export const excludeSiteConfigUpdateRoutes: string[] = [routes.RAW_ITEMS, routes.ACTIVITY_LOG];

export const isExcludeSiteUpdateRoute = (pathname: string) =>
  excludeSiteConfigUpdateRoutes.find((route) => pathname.includes(route));

export const isOrgViewRoute = (pathname: string) => orgRoutes.some((route) => pathname.includes(route));

export const isAllSitesRoute = (pathname: string) => allSitesRoutes.find((route) => pathname.includes(route.basePath));

export const isAllSitesRouteWithOldPermissions = (pathname: string) =>
  allSitesRoutesWithOldPermisssions.find((route) => pathname.includes(route.basePath));

export const isCfcOrgViewRoute = (pathname: string) => cfcOrgRoutes.some((route) => pathname.includes(route));

export const shouldDisableSiteSelection = (pathname: string, isCfcOrg: boolean) => {
  const isJobConfigCfcRoute =
    (!!matchPath(pathname, { path: routes.JOBCODE_CONFIGURATION_DETAILS, exact: true }) ||
      !!matchPath(pathname, { path: routes.JOBCODE_CONFIGURATION_OVERVIEW_PAGE, exact: true }) ||
      !!matchPath(pathname, { path: routes.JOBCODE_EMPLOYEES_OVERVIEW_PAGE, exact: true })) &&
    isCfcOrg;
  return isJobConfigCfcRoute;
};

// Returns true when the current route is the Audit Log page in the CFC context.
// See ASM-9140.
export const isAuditHierarchyCfcRoute = (pathname: string) =>
  auditHierarchyCfcRoutes.some((route) => pathname.includes(route));
